import React, { useEffect, useMemo, useRef, useState } from "react";
import ClientLayoutWithOutHeader from "../../client/components/layout/ClientLayoutWithOutHeaderForTeenPatti";
import TpStudioHeader from "../../client/tp-studio/tp-studio-header";
import TPStudioSidebar from "../../client/tp-studio/TPStudioSidebar";
import "../../../assets/topstudio/styles/CardsSprite.css";
import "../../../assets/topstudio/styles/aviator.css";
import "./Aviator.scoped.css";
import useSocket from "../../../hooks/useSocket";
import axiosInstance from "../../../utils/axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Explosion from "../../../assets/topstudio/images/aviator/aviatorexplosion.gif";
import Newplane from "../../../assets/topstudio/images/aviator/newplane.png";
import BgSound from "../../../assets/topstudio/audio/aviatorbg.mp3";
import CrashSound from "../../../assets/topstudio/audio/Explosion.wav";
import AviatorRules from "./pop-up/aviator-rules";

import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../store/userSlice";
import AvaitorResult from "../../client/tp-studio/tp-result/AvaitorResult.jsx";
import { Check, X } from "lucide-react";

/** Timing & flight settings — same curve as AviatorX */
const STEP = 0.01; // per tick
const STEP_MS = 91; // ms per tick after 1.10
const SLOW_UNTIL = 1.1;
const CASHOUT_MIN_MULTIPLIER = 1;
const SLOW_DURATION_MS = 2000; // 1.00 → 1.10
const SLOW_RANGE_STEPS = Math.round((SLOW_UNTIL - 1) / STEP);
const SLOW_STEP_MS = SLOW_DURATION_MS / SLOW_RANGE_STEPS;
const EXPLOSION_MS = 900; // blast duration
const DROP_MS = 3000; // plane drop animation length
const CLOSED_RESET_MS = 2000; // reset counter 2s after crash/CLOSED
const SERVER_TICK_MS = 200; //added by uk - filler uses 200ms up to 1.10, then 91ms, instead of guessing a 1 second gap
const LUCKY_PLAN_TARGET = 3.5; // dummy cashouts without knowing the crash
const SOUND_MUTED = false;

/** Scroll strip — `public/assets/images/MainN.webp` (query busts browser cache when the file changes) */
const AVIATOR_SCROLL_BG = `${(process.env.PUBLIC_URL || "").replace(
  /\/$/,
  ""
)}/assets/images/MainN.webp?v=${process.env.REACT_APP_AVIATOR_BG_VERSION || "2"
  }`;

const FUN_FACT_OVERLAY_TEXT =
  "FunFact: Inviting your friend doubles your combined chances of winning.";

function formatWinToastAmount(amount) {
  const n = parseFloat(amount);
  if (Number.isNaN(n)) return "0";
  if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
  return n.toFixed(2);
}

function showWinToast(amount) {
  const display = formatWinToastAmount(amount);
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <Check size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">You won {display}</span>
    </div>,
    {
      toastId: "aviator-win-toast",
      className: "av-win-toast",
      icon: false,
      closeButton: false,
      autoClose: 1500,
      hideProgressBar: false,
      position: "top-center"
    }
  );
}

function showLostToast(amount) {
  const display = formatWinToastAmount(amount);
  toast.dismiss("aviator-win-toast");
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <X size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">You lost {display}</span>
    </div>,
    {
      toastId: `aviator-lost-${Date.now()}`,
      className: "av-lost-toast",
      icon: false,
      closeButton: false,
      autoClose: 1500,
      hideProgressBar: false,
      position: "top-center"
    }
  );
}

function showBetPlacedToast(tossId, msg) {
  toast(
    <div className="av-toast-body" key={tossId}>
      <span className="av-toast-icon">
        <Check size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">{msg}</span>
    </div>,
    {
      toastId: "aviator-bet-placed-toast",
      className: "av-bet-toast",
      icon: false,
      closeButton: false,
      autoClose: 1000,
      hideProgressBar: false,
      position: "top-right"
    }
  );
}

const normalizeResultsList = (data) => {
  if (data == null) return [];
  const values = Array.isArray(data) ? data : Object.values(data);
  return values
    .filter((v) => v !== null && v !== undefined && v !== "")
    .map((v) => parseFloat(v))
    .filter((v) => !Number.isNaN(v));
};

const LAUNCH_COUNTDOWN_DEFAULT = 4;
const PRE_FLIGHT_STREAM_WINDOW_MS = (LAUNCH_COUNTDOWN_DEFAULT + 2) * 1000;

const parseServerSeconds = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : null;
};

const RESULT_SYNC_BUFFER = 0.02;

const getTotalFlightSteps = (target) =>
  Math.max(1, Math.round((target - 1) / STEP) + 1);

/** Where the flight clock should stand for a given multiplier (2s slow start, then 91ms ticks). */
const elapsedMsFromMultiplier = (value, skipSlowStart) => {
  const v = Math.max(1, Number(value) || 1);
  if (skipSlowStart) {
    const steps = Math.max(0, Math.round((v - 1) / STEP));
    return steps * STEP_MS;
  }
  if (v <= SLOW_UNTIL + 0.0001) {
    const steps = Math.max(0, Math.round((v - 1) / STEP));
    return steps * SLOW_STEP_MS;
  }
  const extraSteps = Math.max(0, Math.round((v - SLOW_UNTIL) / STEP));
  return SLOW_DURATION_MS + extraSteps * STEP_MS;
};

/** Inverse of elapsedMsFromMultiplier — same curve as PHP / AviatorX. */
const multiplierFromElapsed = (elapsedMs, skipSlowStart) => {
  const ms = Math.max(0, Number(elapsedMs) || 0);
  if (skipSlowStart) {
    return 1 + Math.floor(ms / STEP_MS) * STEP;
  }
  if (ms <= SLOW_DURATION_MS) {
    const steps = Math.min(SLOW_RANGE_STEPS, Math.floor(ms / SLOW_STEP_MS));
    return 1 + steps * STEP;
  }
  const extraSteps = Math.floor((ms - SLOW_DURATION_MS) / STEP_MS);
  return SLOW_UNTIL + extraSteps * STEP;
};

const getFlightDurationSec = (target) =>
  elapsedMsFromMultiplier(target) / 1000;

/** Never exceed crash result; stay a hair below when joining near the end */
const clampSyncedFlightValue = (value, target) => {
  let v = Math.min(Math.max(1, value), target);
  const safeCeiling = +(target - RESULT_SYNC_BUFFER).toFixed(2);
  if (safeCeiling >= 1 && v > safeCeiling) {
    v = safeCeiling;
  }
  return +v.toFixed(2);
};

const multiplierToStep = (value, target) => {
  const totalSteps = getTotalFlightSteps(target);
  const step = Math.round((value - 1) / STEP) + 1;
  return Math.min(totalSteps, Math.max(1, step));
};

const MUSLIM_NAMES = [
  "Muhammad",
  "Ahmed",
  "Ali",
  "Hassan",
  "Hussein",
  "Omar",
  "Usman",
  "Bilal",
  "Hamza",
  "Yusuf",
  "Ibrahim",
  "Khalid",
  "Tariq",
  "Imran",
  "Farhan",
  "Saeed",
  "Rashid",
  "Nadeem",
  "Asif",
  "Faisal",
  "Zubair",
  "Salman",
  "Waqas",
  "Junaid",
  "Aamir",
  "Shahid",
  "Noman",
  "Rehan",
  "Sajid",
  "Kamran",
  "Arif",
  "Javed",
  "Fatima",
  "Aisha",
  "Zainab",
  "Maryam",
  "Khadija",
  "Hafsa",
  "Sana",
  "Hina",
  "Rabia",
  "Nadia",
  "Samina",
  "Shazia",
  "Farah",
  "Sadia",
  "Ayesha",
  "Bushra",
  "Uzma",
  "Nazia",
  "Saima",
  "Rubina",
  "Tahira",
  "Yasmin",
  "Amna",
  "Laiba",
  "Mahnoor",
  "Hira",
  "Kinza",
  "Aliza",
  "Danish",
  "Haroon",
  "Majid",
  "Rizwan"
];

const luckyRandomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const maskLuckyPlayerName = (name) => {
  if (!name) return "****";
  const visible = name.slice(0, Math.min(4, name.length));
  return `${visible}****`;
};

const formatLuckyPlayerAmount = (value) =>
  Number(value).toLocaleString("en-IN");

/** profit = (size × price) − size */
const calcLuckyPlayerProfit = (price, amountRaw) => {
  const priceNum = parseFloat(price);
  const amount = Number(amountRaw);
  if (!Number.isFinite(priceNum) || !Number.isFinite(amount) || amount <= 0) {
    return "";
  }
  const profit = priceNum * amount - amount;
  return formatLuckyPlayerAmount(Math.round(profit));
};

const LUCKY_RESERVE_FRACTION = 0.12;
const LUCKY_MIN_RESERVE = 8;
const LUCKY_MAX_RESERVE = 25;
const LUCKY_MIN_STEP_GAP = 5;

const luckyBetIsOpen = (bet) =>
  !bet.locked && (bet.rate === "--" || bet.profit === "" || bet.profit == null);

const pickRandomOpenBetIds = (bets, count, reserveCount = 0) => {
  const open = bets.filter(luckyBetIsOpen);
  const maxPickable = Math.max(0, open.length - reserveCount);
  if (maxPickable <= 0) return new Set();
  const n = Math.min(count, maxPickable);
  const shuffled = [...open].sort(() => Math.random() - 0.5);
  return new Set(shuffled.slice(0, n).map((bet) => bet.id));
};

const applyRandomLuckyCashoutBatch = (
  bets,
  price,
  batchSize,
  reserveCount = 0
) => {
  const selectedIds = pickRandomOpenBetIds(bets, batchSize, reserveCount);
  if (!selectedIds.size) return bets;
  return bets.map((bet) =>
    selectedIds.has(bet.id) ? lockLuckyBetAtPrice(bet, price) : bet
  );
};

const applyLuckyCashoutEvents = (bets, events, reserveCount) =>
  events.reduce(
    (acc, event) =>
      applyRandomLuckyCashoutBatch(
        acc,
        event.value.toFixed(2),
        event.batchSize,
        reserveCount
      ),
    bets
  );

/** Spread cashouts evenly across the flight; leave a slice of bets blank until the end */
const buildLuckyCashoutPlan = (target, totalBets) => {
  const reserveCount = Math.min(
    totalBets - 1,
    Math.max(
      LUCKY_MIN_RESERVE,
      Math.min(LUCKY_MAX_RESERVE, Math.ceil(totalBets * LUCKY_RESERVE_FRACTION))
    )
  );
  const toCashOut = Math.max(0, totalBets - reserveCount);
  const totalSteps = getTotalFlightSteps(target);
  const startStep = Math.max(1, Math.floor(totalSteps * 0.08));
  const endStep = Math.max(startStep + 1, Math.floor(totalSteps * 0.9));
  const usableSteps = endStep - startStep;
  const maxWaves = Math.max(1, Math.floor(usableSteps / LUCKY_MIN_STEP_GAP));
  const waveCount = Math.min(toCashOut, maxWaves);

  if (waveCount <= 0 || toCashOut <= 0) {
    return { events: [], reserveCount, nextEventIndex: 0 };
  }

  const baseBatch = Math.floor(toCashOut / waveCount);
  let remainder = toCashOut % waveCount;
  const events = [];

  for (let i = 0; i < waveCount; i++) {
    const step =
      waveCount === 1
        ? Math.floor((startStep + endStep) / 2)
        : startStep + Math.round((i / (waveCount - 1)) * usableSteps);
    const value = clampSyncedFlightValue(1 + (step - 1) * STEP, target);
    let batchSize = baseBatch + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    if (batchSize > 0) events.push({ value, batchSize });
  }

  return { events, reserveCount, nextEventIndex: 0 };
};

const lockLuckyBetAtPrice = (bet, price) => {
  if (bet.locked) return bet;
  return {
    ...bet,
    rate: price,
    profit: calcLuckyPlayerProfit(price, bet.amountRaw),
    locked: true
  };
};

const createDummyLuckyBet = () => {
  const baseName = MUSLIM_NAMES[luckyRandomInt(0, MUSLIM_NAMES.length - 1)];
  const name = `${baseName}${luckyRandomInt(10, 99)}`;
  const amount = luckyRandomInt(3, 50) * 1000;

  return {
    id: `live-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    uname: maskLuckyPlayerName(name),
    amount: formatLuckyPlayerAmount(amount),
    amountRaw: amount,
    rate: "--",
    profit: ""
  };
};

const createDummyBetQueue = () => {
  const count = luckyRandomInt(100, 180);
  return Array.from({ length: count }, createDummyLuckyBet);
};

const parseLuckyPlayerEntry = (
  entry,
  { isUser = false, betId = null } = {}
) => {
  try {
    const obj = typeof entry === "string" ? JSON.parse(entry) : entry;
    const id = betId || obj._id || obj.bet_id;
    if (!id) return null;

    const name = obj.uname || "";
    const amountRaw = Number(obj.lockamt || obj.delivery || obj.amount || 0);
    const rateVal = obj.rate;
    const rateNum = parseFloat(rateVal);
    const hasCashout =
      rateVal != null &&
      rateVal !== "" &&
      rateVal !== "-" &&
      rateVal !== "--" &&
      Number.isFinite(rateNum) &&
      rateNum > 1;
    const rate = hasCashout ? Number(rateNum).toFixed(2) : "--";
    const profit =
      hasCashout && amountRaw ? calcLuckyPlayerProfit(rate, amountRaw) : "";

    return {
      id: String(id),
      betId: id,
      sectionNo: obj.section_no,
      uname: maskLuckyPlayerName(name),
      amount: formatLuckyPlayerAmount(amountRaw),
      amountRaw,
      rate,
      profit,
      locked: hasCashout,
      isUser
    };
  } catch {
    return null;
  }
};

const buildUserRecordsFromReturnBets = (returnBets, amounts, displayName) =>
  returnBets
    .filter((bet) => bet?.bet_id)
    .map((returnBet, idx) => {
      const betId = String(returnBet.bet_id);
      const amountRaw = Number(amounts[idx] || 0);
      return {
        id: betId,
        betId,
        sectionNo: returnBet.section_no,
        uname: maskLuckyPlayerName(displayName),
        amount: formatLuckyPlayerAmount(amountRaw),
        amountRaw,
        rate: "--",
        profit: "",
        locked: false,
        isUser: true
      };
    });

/**
 * Mid-flight reload sync — left_time is elapsed seconds since flight started.
 * Maps elapsed time onto the result-scaled flight window (not game_time).
 */
const getFlightSyncFromLeftTime = (target, leftTime) => {
  const elapsed = parseServerSeconds(leftTime);
  if (elapsed == null) return null;

  const flightDurationSec = getFlightDurationSec(target);
  if (flightDurationSec <= 0) return null;

  const elapsedSec = Math.max(0, Math.min(flightDurationSec, elapsed));
  const progress = elapsedSec / flightDurationSec;

  let value = 1 + progress * (target - 1);
  value = clampSyncedFlightValue(value, target);

  const step = multiplierToStep(value, target);
  const finalValue = clampSyncedFlightValue(
    +(1 + (step - 1) * STEP).toFixed(2),
    target
  );
  const finalStep = multiplierToStep(finalValue, target);

  return {
    step: finalStep,
    value: finalValue,
    elapsedMs: elapsedMsFromMultiplier(finalValue)
  };
};

export default function Aviator() {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.user);
  const [showRules, setShowRules] = useState(false);
  const [open, setOpen] = useState(false);
  const [scene, setScene] = useState("loading");
  const [launchCountdown, setLaunchCountdown] = useState(4);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(1);

  const sessionUser = useSelector((s) => s.userStore.data);

  /** Flight state */
  const [running, setRunning] = useState(false);
  const [flightValue, setFlightValue] = useState(0);

  /** Explosion */
  const [showExplosion, setShowExplosion] = useState(false);
  const [isDropping, setIsDropping] = useState(false);
  const [isAudioOmitted, setIsAudioOmitted] = useState(false);

  /** Audio */
  const bgAudioRef = useRef(null);
  const crashAudioRef = useRef(null);

  const rafRef = useRef(null);
  const lastStepRef = useRef(-1);
  const startTsRef = useRef(null);
  const counterRawRef = useRef(null);
  const capTsRef = useRef(0);
  const capFromRef = useRef(1);
  const capIntervalRef = useRef(SERVER_TICK_MS);
  const skipStartHoldRef = useRef(false);

  const [scrollVersion, setScrollVersion] = useState(0);

  /** added by uk - show the server running_result. Do not build our own multiplier. Crash only when the round is CLOSED. */
  const maxFlightRef = useRef(null);
  const crashKnownRef = useRef(false);
  const lastServerRunningRef = useRef(1);

  /** ===== API / Betting state (LEFT side - bak / "b") ===== */
  const [leftBetAmount, setLeftBetAmount] = useState(100);
  const [leftBetErrorMessage, setLeftBetErrorMessage] = useState({});
  const [leftBetButtonShow, setLeftBetButtonShow] = useState(true);
  const [leftBetArray, setLeftBetArray] = useState([]);
  const leftBetArrayRef = useRef([]);
  const [leftMainBetId, setLeftMainBetId] = useState("");
  const [leftCashOutAmount, setLeftCashOutAmount] = useState("");
  const [leftShowCashoutButton, setLeftShowCashoutButton] = useState(false);
  const [leftIsCashingOut, setLeftIsCashingOut] = useState(false);
  const leftBetPlacedRef = useRef(false);
  const [leftBetGameId, setLeftBetGameId] = useState("");
  const leftMainBetIdRef = useRef("");
  const leftBetGameIdRef = useRef("");
  const leftBetAmountRef = useRef(leftBetAmount);
  const leftIsCashingOutRef = useRef(false);

  /** ===== API / Betting state (RIGHT side - lay / "l") ===== */
  const [rightBetAmount, setRightBetAmount] = useState(100);
  const [rightBetErrorMessage, setRightBetErrorMessage] = useState({});
  const [rightBetButtonShow, setRightBetButtonShow] = useState(true);
  const [rightBetArray, setRightBetArray] = useState([]);
  const rightBetArrayRef = useRef([]);
  const [rightMainBetId, setRightMainBetId] = useState("");
  const [rightCashOutAmount, setRightCashOutAmount] = useState("");
  const [rightShowCashoutButton, setRightShowCashoutButton] = useState(false);
  const [rightIsCashingOut, setRightIsCashingOut] = useState(false);
  const rightBetPlacedRef = useRef(false);
  const [rightBetGameId, setRightBetGameId] = useState("");
  const rightMainBetIdRef = useRef("");
  const rightBetGameIdRef = useRef("");
  const rightBetAmountRef = useRef(rightBetAmount);
  const rightIsCashingOutRef = useRef(false);
  const cashOutNowRef = useRef(null);

  /** ===== Auto Bet / Auto Cashout state ===== */
  const [leftAutoBet, setLeftAutoBet] = useState(false);
  const [rightAutoBet, setRightAutoBet] = useState(false);
  const [leftAutoCashout, setLeftAutoCashout] = useState(false);
  const [rightAutoCashout, setRightAutoCashout] = useState(false);
  const [leftAutoCashoutAt, setLeftAutoCashoutAt] = useState(1.1);
  const [rightAutoCashoutAt, setRightAutoCashoutAt] = useState(1.1);

  const leftAutoBetRef = useRef(false);
  const rightAutoBetRef = useRef(false);
  const leftAutoCashoutRef = useRef(false);
  const rightAutoCashoutRef = useRef(false);
  const leftAutoCashoutAtRef = useRef(1.1);
  const rightAutoCashoutAtRef = useRef(1.1);

  const isPostCrashScene = () => {
    const s = sceneRef.current;
    return (
      s === "crash-explode" || s === "crash-drop" || s === "finished"
    );
  };

  const tryAutoCashoutAtValue = (currentValue) => {
    const value = Number(currentValue);
    if (!(value + 0.0001 >= CASHOUT_MIN_MULTIPLIER)) return;
    for (const side of ["left", "right"]) {
      const autoOn =
        side === "left"
          ? leftAutoCashoutRef.current
          : rightAutoCashoutRef.current;
      const autoAt = parseFloat(
        side === "left"
          ? leftAutoCashoutAtRef.current
          : rightAutoCashoutAtRef.current
      );
      const placed =
        side === "left"
          ? leftBetPlacedRef.current
          : rightBetPlacedRef.current;
      const cashing =
        side === "left"
          ? leftIsCashingOutRef.current
          : rightIsCashingOutRef.current;
      if (!autoOn || !placed || cashing) continue;
      if (!(autoAt > 0) || value + 0.0001 < autoAt) continue;
      cashOutNowRef.current?.(side, autoAt);
    }
  };

  const showLostToastForUncashedBets = (evtId = roundIdRef.current) => {
    if (evtId && lostToastEvtRef.current === evtId) return;

    let lostAmount = 0;
    if (leftBetPlacedRef.current && !leftIsCashingOutRef.current) {
      lostAmount += Number(leftBetAmountRef.current) || 0;
    }
    if (rightBetPlacedRef.current && !rightIsCashingOutRef.current) {
      lostAmount += Number(rightBetAmountRef.current) || 0;
    }
    if (lostAmount <= 0) return;

    if (evtId) lostToastEvtRef.current = evtId;
    pendingLostToastEvtRef.current = null;
    showLostToast(lostAmount);
  };

  /** ===== Shared state ===== */
  const [roundId, setRoundId] = useState("");
  const [userLuckyRecords, setUserLuckyRecords] = useState([]);
  const [liveBets, setLiveBets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const betsList = useMemo(
    () => [...userLuckyRecords, ...liveBets],
    [userLuckyRecords, liveBets]
  );
  const luckyPlayerCount = betsList.length;

  const isLoadGameRef = useRef(0);
  const lastCrashedEvtIdRef = useRef(null);
  const lastFinishedEvtIdRef = useRef(null);
  const lastKnownEvtIdRef = useRef(null);
  const dropTimeoutRef = useRef(null);
  const crashTimeoutRef = useRef(null);
  const roundIdRef = useRef("");
  const sceneRef = useRef("loading");
  const closedResetRef = useRef(null);
  const lastLuckyPlayersEvtIdRef = useRef(null);
  const lastFlightStartedEvtIdRef = useRef(null);
  const lastCountdownResetEvtRef = useRef(null);
  const betPlacedToastEvtRef = useRef(null);
  const lostToastEvtRef = useRef(null);
  const pendingLostToastEvtRef = useRef(null);
  const placingBetsRef = useRef(false);
  const placeAllBetsNowRef = useRef(null);
  const isAudioOmittedRef = useRef(isAudioOmitted);
  isAudioOmittedRef.current = isAudioOmitted;
  const luckyStreamTimerRef = useRef(null);
  const luckyCashoutTimerRef = useRef(null);
  const luckyCashoutPlanRef = useRef(null);
  const luckyPendingQueueRef = useRef([]);
  const userPlacedBetIdsRef = useRef(new Set());
  const userPlacedBetIdsOrderRef = useRef([]);
  const flightValueRef = useRef(flightValue);

  useEffect(() => {
    leftBetAmountRef.current = leftBetAmount;
  }, [leftBetAmount]);
  useEffect(() => {
    rightBetAmountRef.current = rightBetAmount;
  }, [rightBetAmount]);

  /** Track disconnection so we can reset on reconnect */
  const wasDisconnectedRef = useRef(false);
  const [resultData, setResultData] = useState([]);

  const lastTenResults = useMemo(
    () => normalizeResultsList(resultData).slice(0, 10),
    [resultData]
  );

  const activeTab = async (tab1) => {
    setActiveTabIndex(tab1);
  };

  /** Hard-reset everything back to waiting phase */
  const resetToWaiting = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (dropTimeoutRef.current) {
      clearTimeout(dropTimeoutRef.current);
      dropTimeoutRef.current = null;
    }
    if (closedResetRef.current) {
      clearTimeout(closedResetRef.current);
      closedResetRef.current = null;
    }
    setRunning(false);
    setShowExplosion(false);
    setIsDropping(false);
    setFlightValue(0);
    maxFlightRef.current = null;
    crashKnownRef.current = false;
    lastServerRunningRef.current = 1;
    startTsRef.current = null;
    counterRawRef.current = null;
    capTsRef.current = 0;
    capFromRef.current = 1;
    capIntervalRef.current = SERVER_TICK_MS;
    skipStartHoldRef.current = false;
    setScrollVersion((v) => v + 1);
    setLeftBetButtonShow(true);
    setRightBetButtonShow(true);
    // NOTE: Do NOT clear lastFinishedEvtIdRef, lastFlightStartedEvtIdRef, or isLoadGameRef here.
    // They protect against stale/replayed server data after a brief data gap.
    // They are cleared only when a genuinely new round (new evt_id, start==0) arrives.
    setScene("waiting");
    sceneRef.current = "waiting";
  };

  /** ===== Socket ===== */
  const { data, isConnected } = useSocket({
    marketId: null,
    mtype: "teenpatti",
    type: "aviator"
  });

  // ===== Socket reconnect handling =====
  useEffect(() => {
    if (!isConnected) {
      wasDisconnectedRef.current = true;
      // Pause the flight ticker while disconnected
      setRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    // Socket just came back online
    if (wasDisconnectedRef.current) {
      wasDisconnectedRef.current = false;
      //console.log("Socket reconnected \u2013 resetting Aviator state");

      // Reset the game to the loading state so server data drives the next scene
      setFlightValue(0);
      setShowExplosion(false);
      setIsDropping(false);
      setScene("loading");
      sceneRef.current = "loading";
      isLoadGameRef.current = 0;
      lastCrashedEvtIdRef.current = null;
      lastFinishedEvtIdRef.current = null;
      //added by uk - the socket drop left this round marked as already flying, so the plane never started again.
      lastFlightStartedEvtIdRef.current = null;
      crashKnownRef.current = false;
      setScrollVersion((v) => v + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  //console.log("data: ", data);
  //console.log("start: ", data?.data?.data?.start);
  //console.log("evt_id: ", data?.data?.data?.evt_id);
  //console.log("result: ", data?.data?.data?.result);

  const payloadResult = data?.data?.result;
  //console.log("payloadResult: ", payloadResult);
  //console.log("payloadResult: ", payloadResult);

  const rawInner = data?.data;
  const payload = rawInner?.data?.evt_id
    ? rawInner.data
    : rawInner?.evt_id
      ? rawInner
      : null;
  const userName = userInfo?.data?.uname || data?.user?.uname || "You";
  const payloadLuckyPlayers = rawInner?.data?.evt_id
    ? rawInner.lucky_players
    : data?.lucky_players;

  const clearLuckyPlayersStream = () => {
    if (luckyStreamTimerRef.current) {
      clearTimeout(luckyStreamTimerRef.current);
      luckyStreamTimerRef.current = null;
    }
    if (luckyCashoutTimerRef.current) {
      clearInterval(luckyCashoutTimerRef.current);
      luckyCashoutTimerRef.current = null;
    }
  };

  const isInFlightScene = () =>
    sceneRef.current === "flight" ||
    sceneRef.current === "crash-explode" ||
    sceneRef.current === "crash-drop" ||
    sceneRef.current === "finished";

  const getPreFlightStreamDelay = (remainingCount) => {
    const base = Math.max(
      20,
      Math.floor(PRE_FLIGHT_STREAM_WINDOW_MS / Math.max(remainingCount, 1))
    );
    return luckyRandomInt(Math.max(15, base - 10), base + 20);
  };

  const flushRemainingDummyBets = () => {
    const queue = luckyPendingQueueRef.current;
    if (!queue.length) return;
    luckyPendingQueueRef.current = [];
    setLiveBets((prev) => [...queue, ...prev]);
  };

  const stopDummyBetStream = () => {
    if (luckyStreamTimerRef.current) {
      clearTimeout(luckyStreamTimerRef.current);
      luckyStreamTimerRef.current = null;
    }
  };

  const finalizeDummyBetsBeforeFlight = () => {
    stopDummyBetStream();
    flushRemainingDummyBets();
  };

  const scheduleNextLiveBet = () => {
    if (isInFlightScene()) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const queue = luckyPendingQueueRef.current;
    if (!queue.length) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const batchSize = luckyRandomInt(2, 5);
    const batch = queue.splice(0, Math.min(batchSize, queue.length));
    setLiveBets((prev) => [...batch, ...prev]);

    if (!queue.length) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const delay = getPreFlightStreamDelay(queue.length);
    luckyStreamTimerRef.current = setTimeout(scheduleNextLiveBet, delay);
  };

  const startLiveLuckyPlayersStream = () => {
    clearLuckyPlayersStream();
    luckyPendingQueueRef.current = createDummyBetQueue();
    userPlacedBetIdsRef.current = new Set();
    userPlacedBetIdsOrderRef.current = [];
    setUserLuckyRecords([]);
    setLiveBets([]);
    scheduleNextLiveBet();
  };

  const beginFlight = ({
    target,
    leftTime,
    runningResult,
    syncProgress = false,
    force = false
  }) => {
    const evtId = roundIdRef.current;
    const postCrashScenes = ["crash-explode", "crash-drop", "finished"];

    if (evtId && lastFinishedEvtIdRef.current === evtId) return;
    if (evtId && lastCrashedEvtIdRef.current === evtId) return;
    if (evtId && lastFlightStartedEvtIdRef.current === evtId) return;
    if (!force && postCrashScenes.includes(sceneRef.current)) return;

    lastFlightStartedEvtIdRef.current = evtId;
    luckyCashoutPlanRef.current = null;
    finalizeDummyBetsBeforeFlight();

    crashKnownRef.current = false;
    const running = parseFloat(runningResult);
    const startValue = Number.isFinite(running) && running >= 1 ? running : 1;
    lastServerRunningRef.current = startValue;
    maxFlightRef.current = startValue;

    const isLateJoin = Number.isFinite(running) && running > 1.15;

    skipStartHoldRef.current = !!isLateJoin;
    capTsRef.current = performance.now();
    capFromRef.current = isLateJoin ? startValue : 1;
    capIntervalRef.current = SERVER_TICK_MS;

    if (isLateJoin) {
      flightValueRef.current = startValue;
      counterRawRef.current = startValue;
      setFlightValue(startValue);
      lastStepRef.current = multiplierToStep(startValue, startValue);
      startTsRef.current =
        performance.now() -
        elapsedMsFromMultiplier(startValue, skipStartHoldRef.current);
    } else {
      flightValueRef.current = 1;
      counterRawRef.current = 1;
      setFlightValue(1);
      lastStepRef.current = -1;
      startTsRef.current = null;
    }

    setScrollVersion((v) => v + 1);
    setScene("flight");
    sceneRef.current = "flight";
    setRunning(true);
  };

  const startTakeoffAfterBets = async ({
    target,
    leftTime,
    runningResult,
    syncProgress = false,
    force = false
  }) => {
    const evtId = roundIdRef.current;
    const evtStatus = payload?.evt_status ?? payload?.status;
    if (evtStatus === "CLOSED") return;
    if (evtId && lastFinishedEvtIdRef.current === evtId) return;
    if (evtId && lastCrashedEvtIdRef.current === evtId) return;
    if (!force && evtId && lastFlightStartedEvtIdRef.current === evtId) return;

    beginFlight({
      target,
      leftTime,
      runningResult,
      syncProgress,
      force
    });
    if (!SOUND_MUTED && isAudioOmittedRef.current) {
      bgAudioRef.current?.play()?.catch(() => { });
    } else {
      bgAudioRef.current?.pause();
    }
  };

  const updateUserData = async (userData) => {
    if (!userData || !sessionUser) return;
    await dispatch(
      setUserData({
        ...sessionUser,
        fname: userData.fname ?? sessionUser.fname,
        uname: userData.uname ?? sessionUser.uname,
        bz_balance: userData.bz_balance ?? sessionUser.bz_balance,
        opin_bal: userData.opin_bal ?? sessionUser.opin_bal,
        user_role: sessionUser.user_role,
        _id: sessionUser._id
      })
    );
  };
  /** ===== Sync payload -> state ===== */

  useEffect(() => {
    if (payloadResult !== null && payloadResult !== undefined) {
      setResultData(payloadResult);
    }
  }, [payloadResult]);

  useEffect(() => {
    if (!payload) return;

    updateUserData(data?.user);

    // Skip payloads if the evt_id is the same as the last finished round
    if (payload.evt_id && payload.evt_id === lastFinishedEvtIdRef.current)
      return;

    if (payload.evt_id) {
      setRoundId(payload.evt_id);
      roundIdRef.current = payload.evt_id;
      lastKnownEvtIdRef.current = payload.evt_id;
    }

    // Bet button enable/disable based on game state
    const evtStatus = payload.evt_status ?? payload.status;
    if (payload.start == 0) {
      const incomingEvtId = payload.evt_id;
      const flyingThisRound =
        incomingEvtId &&
        lastFlightStartedEvtIdRef.current &&
        String(lastFlightStartedEvtIdRef.current) === String(incomingEvtId) &&
        (sceneRef.current === "flight" ||
          sceneRef.current === "crash-explode" ||
          sceneRef.current === "crash-drop");
      // A late start==0 for the round that already took off would hide Cashout
      // for about a second and then bring it back. Ignore it.
      if (flyingThisRound) {
        return;
      }
      const isNewCountdownRound =
        incomingEvtId && incomingEvtId !== lastCountdownResetEvtRef.current;

      // Next evt can open while this client is still flying/crashing the last one.
      // Close that flight against the old id so it cannot mark the new round finished.
      if (isNewCountdownRound && sceneRef.current === "flight") {
        const flyingEvtId = lastFlightStartedEvtIdRef.current;
        if (flyingEvtId && flyingEvtId !== incomingEvtId) {
          lastFinishedEvtIdRef.current = flyingEvtId;
          lastCrashedEvtIdRef.current = flyingEvtId;
          const result = maxFlightRef.current;
          if (result != null && result > 0) {
            setResultData((prev) => {
              const list = normalizeResultsList(prev);
              if (list.length > 0 && list[list.length - 1] === result) {
                return prev;
              }
              return [...list, result];
            });
          }
          pendingLostToastEvtRef.current = flyingEvtId;
          showLostToastForUncashedBets(flyingEvtId);
        }
        setRunning(false);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      }

      lastFlightStartedEvtIdRef.current = null;
      if (lastCrashedEvtIdRef.current === incomingEvtId) {
        lastCrashedEvtIdRef.current = null;
      }
      crashKnownRef.current = false;
      lastServerRunningRef.current = 1;
      startTsRef.current = null;
      counterRawRef.current = null;
      capTsRef.current = 0;
      capFromRef.current = 1;
      capIntervalRef.current = SERVER_TICK_MS;
      skipStartHoldRef.current = false;
      // Cancel any pending CLOSED reset since server is already starting fresh
      if (closedResetRef.current) {
        clearTimeout(closedResetRef.current);
        closedResetRef.current = null;
      }
      if (dropTimeoutRef.current) {
        clearTimeout(dropTimeoutRef.current);
        dropTimeoutRef.current = null;
      }

      // Transition from loading/waiting to countdown on new evt_id
      if (
        sceneRef.current === "loading" ||
        sceneRef.current === "waiting" ||
        sceneRef.current === "finished" ||
        sceneRef.current === "crash-explode" ||
        sceneRef.current === "crash-drop" ||
        sceneRef.current === "flight"
      ) {
        setLaunchCountdown(LAUNCH_COUNTDOWN_DEFAULT);
        setFlightValue(0);
        setShowExplosion(false);
        setIsDropping(false);
        setScrollVersion((v) => v + 1);
        setScene("countdown");
        sceneRef.current = "countdown";
      }

      if (isNewCountdownRound) {
        lastCountdownResetEvtRef.current = payload.evt_id;
        betPlacedToastEvtRef.current = null;
        lostToastEvtRef.current = null;

        setLeftBetButtonShow(true);
        setLeftShowCashoutButton(false);
        setLeftMainBetId("");
        leftMainBetIdRef.current = "";
        setLeftCashOutAmount("");
        leftBetPlacedRef.current = false;
        setLeftBetGameId("");
        leftBetGameIdRef.current = "";
        if (leftBetArrayRef.current.length === 0) setLeftBetErrorMessage({});

        setRightBetButtonShow(true);
        setRightShowCashoutButton(false);
        setRightMainBetId("");
        rightMainBetIdRef.current = "";
        setRightCashOutAmount("");
        rightBetPlacedRef.current = false;
        setRightBetGameId("");
        rightBetGameIdRef.current = "";
        if (rightBetArrayRef.current.length === 0) setRightBetErrorMessage({});
      }

      if (
        payload.evt_id &&
        payload.evt_id !== lastLuckyPlayersEvtIdRef.current
      ) {
        lastLuckyPlayersEvtIdRef.current = payload.evt_id;
        startLiveLuckyPlayersStream();
      }

      if (
        isNewCountdownRound &&
        leftAutoBetRef.current &&
        leftBetArrayRef.current.length === 0 &&
        !leftBetPlacedRef.current
      ) {
        handleBetClick("left");
      }
      if (
        isNewCountdownRound &&
        rightAutoBetRef.current &&
        rightBetArrayRef.current.length === 0 &&
        !rightBetPlacedRef.current
      ) {
        handleBetClick("right");
      }

      if (isNewCountdownRound) {
        const flushLeft =
          leftBetArrayRef.current.length > 0 && !leftBetPlacedRef.current;
        const flushRight =
          rightBetArrayRef.current.length > 0 && !rightBetPlacedRef.current;
        if (flushLeft) {
          setLeftBetErrorMessage({ status: true, message: "Placing bet..." });
        }
        if (flushRight) {
          setRightBetErrorMessage({ status: true, message: "Placing bet..." });
        }
        if (flushLeft || flushRight) {
          placeAllBetsNowRef.current?.(flushLeft, flushRight);
        }
      }
    } else if (payload.start == 1 && evtStatus !== "CLOSED") {
      // Join from running_result. The crash is not on the live feed.
      if (
        (sceneRef.current === "loading" || sceneRef.current === "waiting") &&
        lastFlightStartedEvtIdRef.current !== payload.evt_id
      ) {
        startTakeoffAfterBets({
          target: parseFloat(payload.running_result) || 1,
          runningResult: payload.running_result,
          leftTime: payload.left_time,
          syncProgress: true
        });
      }

      // Only disable bet button for a side that has a queued/placed bet
      if (
        !leftIsCashingOutRef.current &&
        (leftBetArrayRef.current.length > 0 || leftBetPlacedRef.current)
      ) {
        setLeftBetButtonShow(false);
      }
      if (
        !rightIsCashingOutRef.current &&
        (rightBetArrayRef.current.length > 0 || rightBetPlacedRef.current)
      ) {
        setRightBetButtonShow(false);
      }

      isLoadGameRef.current = 1;

      // Show cashout buttons if bets were placed
      if (
        !leftIsCashingOutRef.current &&
        leftBetPlacedRef.current &&
        (leftMainBetId || leftMainBetIdRef.current)
      ) {
        setLeftShowCashoutButton(true);
      }
      if (
        !rightIsCashingOutRef.current &&
        rightBetPlacedRef.current &&
        (rightMainBetId || rightMainBetIdRef.current)
      ) {
        setRightShowCashoutButton(true);
      }
    }

    // Reset isLoadGame on new countdown
    if (payload.start == 0) {
      isLoadGameRef.current = 0;
    }
  }, [payload]);

  useEffect(() => {
    if (!payload || payload.start != 1) return;
    const evtStatus = payload.evt_status ?? payload.status;
    if (evtStatus === "CLOSED") return;
    const srv = parseFloat(payload.running_result);
    if (!Number.isFinite(srv) || srv < 1) return;

    const nowTs = performance.now();
    const shown = Number.isFinite(counterRawRef.current)
      ? counterRawRef.current
      : 1;
    if (capTsRef.current > 0 && srv > lastServerRunningRef.current + 0.0001) {
      const measured = nowTs - capTsRef.current;
      if (measured >= 20 && measured <= 1500) {
        capIntervalRef.current = measured;
      }
    }
    capTsRef.current = nowTs;
    //added by uk - do not jump straight to the new server number.
    //added by uk - start from the number already on screen and climb up to it. This hides jumps like 0.05.
    capFromRef.current = Math.min(shown, srv);
    lastServerRunningRef.current = srv;
    maxFlightRef.current = srv;
  }, [
    payload?.running_result,
    payload?.start,
    payload?.evt_status,
    payload?.evt_id
  ]);

  /** ===== Lucky Players: stream in live-style (100–180), user bet pinned at top ===== */
  useEffect(() => {
    startLiveLuckyPlayersStream();
    return () => clearLuckyPlayersStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Sync placed user bets from socket lucky_players (actual server bet records) */
  useEffect(() => {
    const placedOrder = userPlacedBetIdsOrderRef.current;
    if (
      !placedOrder.length ||
      !payloadLuckyPlayers ||
      typeof payloadLuckyPlayers !== "object"
    ) {
      return;
    }

    setUserLuckyRecords((prev) => {
      const next = placedOrder
        .map((betId) => {
          const existing = prev.find(
            (bet) => String(bet.betId) === String(betId)
          );
          if (existing?.locked) return existing;

          const entry = payloadLuckyPlayers[betId];
          if (entry) {
            const parsed = parseLuckyPlayerEntry(entry, {
              isUser: true,
              betId
            });
            if (parsed) {
              if (sceneRef.current !== "flight" || !parsed.locked) {
                return {
                  ...parsed,
                  rate: "--",
                  profit: "",
                  locked: false
                };
              }
              return parsed;
            }
          }
          return existing;
        })
        .filter(Boolean);

      return next.length ? next : prev;
    });
  }, [payloadLuckyPlayers]);

  useEffect(() => {
    flightValueRef.current = flightValue;
  }, [flightValue]);

  /** During flight: cash out dummy bets on a schedule spread across the round */
  useEffect(() => {
    if (scene !== "flight" || flightValue < 1) {
      if (scene !== "flight") luckyCashoutPlanRef.current = null;
      return;
    }

    if (!luckyCashoutPlanRef.current) {
      setLiveBets((prev) => {
        if (prev.length > 0) {
          luckyCashoutPlanRef.current = buildLuckyCashoutPlan(
            LUCKY_PLAN_TARGET,
            prev.length
          );
        }
        return prev;
      });
      if (!luckyCashoutPlanRef.current) return;
    }

    const plan = luckyCashoutPlanRef.current;
    const pendingEvents = [];

    while (
      plan.nextEventIndex < plan.events.length &&
      flightValue + 0.0001 >= plan.events[plan.nextEventIndex].value
    ) {
      pendingEvents.push(plan.events[plan.nextEventIndex]);
      plan.nextEventIndex += 1;
    }

    if (!pendingEvents.length) return;

    setLiveBets((prev) =>
      applyLuckyCashoutEvents(prev, pendingEvents, plan.reserveCount)
    );
  }, [scene, flightValue]);

  // Keep sceneRef in sync
  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  /** ===== When status becomes CLOSED, trigger crash immediately (once per evt_id) ===== */
  useEffect(() => {
    const status = payload?.evt_status ?? payload?.status;
    const evtId = payload?.evt_id;
    if (
      status === "CLOSED" &&
      evtId &&
      lastCrashedEvtIdRef.current !== evtId &&
      (sceneRef.current === "flight" || sceneRef.current === "countdown")
    ) {
      const result = parseFloat(payload?.result);
      if (!Number.isFinite(result) || result <= 0) return; // wait until result is public

      lastCrashedEvtIdRef.current = evtId;
      crashKnownRef.current = true;
      maxFlightRef.current = result;
      lastServerRunningRef.current = Math.max(
        lastServerRunningRef.current || 1,
        result
      );

      //added by uk - when the round closes, show the crash right away. The old code waited and caught up, which looked like a hang.
      counterRawRef.current = result;
      flightValueRef.current = result;
      lastStepRef.current = result;
      setFlightValue(result);
      triggerCrash();

      // Schedule a hard reset 2s after CLOSED, regardless of animation state
      if (closedResetRef.current) clearTimeout(closedResetRef.current);
      closedResetRef.current = setTimeout(resetToWaiting, CLOSED_RESET_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payload?.evt_status,
    payload?.status,
    payload?.evt_id,
    payload?.result
  ]);

  /** ===== Countdown ===== */
  useEffect(() => {
    if (scene !== "countdown") return;

    // Take off the moment the server does, even if the local countdown has time
    // left. Short rounds are over in a second and cannot wait for it.
    if (payload?.start == 1) {
      // Don't replay a round that already finished — wait for a new evt_id
      if (payload?.evt_id && payload.evt_id === lastFinishedEvtIdRef.current)
        return;
      // Don't start flight if the current event is already CLOSED
      const evtStatus = payload?.evt_status ?? payload?.status;
      if (evtStatus === "CLOSED") return;
      if (lastFlightStartedEvtIdRef.current === payload?.evt_id) return;

      startTakeoffAfterBets({
        target: parseFloat(payload?.running_result) || 1,
        runningResult: payload?.running_result,
        syncProgress: true
      });
      return;
    }

    if (launchCountdown <= 0) return; // wait for the server to start the round

    const t = setTimeout(() => setLaunchCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [scene, launchCountdown, payload?.start, payload?.running_result]);

  /** ===== Flight ticker (only during "flight") ===== */
  useEffect(() => {
    if (scene !== "flight" || !running) return;

    // Track whether the actual counting has started (target became valid)
    let countingStarted = false;

    const tick = (now) => {
      const evtId = roundIdRef.current;
      if (evtId && lastFinishedEvtIdRef.current === evtId) {
        setRunning(false);
        return;
      }

      const target = maxFlightRef.current;

      // Spin-wait until maxFlightRef has a valid value
      if (target == null || target <= 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // First tick — fresh flight or mid-round join
      if (!countingStarted) {
        countingStarted = true;
        if (startTsRef.current == null) startTsRef.current = now;
        if (lastStepRef.current < 0) lastStepRef.current = 0;
      }

      const cap = lastServerRunningRef.current;
      const safeCap = Number.isFinite(cap) && cap >= 1 ? cap : 1;
      const from = Math.max(
        1,
        Number.isFinite(capFromRef.current) ? capFromRef.current : 1
      );
      const sinceCap = Math.max(0, now - (capTsRef.current || now));
      //added by uk - filler. Walk one cent at a time up to the server number.
      //added by uk - 200ms per cent until 1.10, then 91ms. Same speed as the old Aviator counter.
      const cents = Math.max(0, Math.round((safeCap - from) * 100));
      const stepMs = safeCap <= 1.1 ? 200 : 91;
      const catchWindowMs = Math.max(40, cents * stepMs);
      const progress = Math.min(1, sinceCap / catchWindowMs);
      let raw = from + (safeCap - from) * progress;
      raw = Math.min(safeCap, raw);
      const prevRaw = counterRawRef.current;
      if (Number.isFinite(prevRaw) && prevRaw > raw) raw = Math.min(prevRaw, safeCap);

      counterRawRef.current = raw;
      const next = Math.max(1, Math.floor(raw * 100) / 100);
      flightValueRef.current = next;

      tryAutoCashoutAtValue(Math.min(next, safeCap));

      // Hold at running_result until CLOSED publishes result. Do not crash on the cap.
      if (crashKnownRef.current && next + 0.001 >= (target || safeCap)) {
        lastStepRef.current = target || safeCap;
        flightValueRef.current = target || safeCap;
        setFlightValue(target || safeCap);
        triggerCrash();
        return;
      }

      if (next !== lastStepRef.current) {
        lastStepRef.current = next;
        setFlightValue(next);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, running]);

  /** ===== Crash pipeline ===== */
  const triggerCrash = () => {
    const crashedEvtId =
      lastFlightStartedEvtIdRef.current || roundIdRef.current;
    if (crashedEvtId) {
      lastFinishedEvtIdRef.current = crashedEvtId;
    }
    setRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const result = maxFlightRef.current;
    if (result != null && result > 0) {
      setResultData((prev) => {
        const list = normalizeResultsList(prev);
        if (list.length > 0 && list[list.length - 1] === result) return prev;
        return [...list, result];
      });
    }

    setScene("crash-explode");
    sceneRef.current = "crash-explode";
    setShowExplosion(true);
    crashAudioRef.current?.pause();
    if (!SOUND_MUTED) {
      crashAudioRef.current?.play()?.catch(() => { });
    }

    tryAutoCashoutAtValue(result);
    pendingLostToastEvtRef.current = crashedEvtId;
    showLostToastForUncashedBets(crashedEvtId);

    // Hard reset 2s after crash — overrides explosion/drop animations
    if (closedResetRef.current) clearTimeout(closedResetRef.current);
    closedResetRef.current = setTimeout(resetToWaiting, CLOSED_RESET_MS);

    // After blast time, switch to drop
    if (crashTimeoutRef.current) clearTimeout(crashTimeoutRef.current);
    crashTimeoutRef.current = setTimeout(() => {
      crashTimeoutRef.current = null;
      setShowExplosion(false);
      setIsDropping(true);
      setScene("crash-drop");
      sceneRef.current = "crash-drop";

      // Safety timeout: if CSS animationend never fires, force recovery
      dropTimeoutRef.current = setTimeout(() => {
        setIsDropping((prev) => {
          if (prev) {
            setTimeout(resetToWaiting, 10);
            return false;
          }
          return prev;
        });
      }, DROP_MS + 500);
    }, EXPLOSION_MS);
  };

  /** ===== End of drop animation ===== */
  const handleDropEnd = () => {
    if (isDropping) {
      // Clear the safety timeout since animation fired normally
      if (dropTimeoutRef.current) {
        clearTimeout(dropTimeoutRef.current);
        dropTimeoutRef.current = null;
      }
      setIsDropping(false);
      setScene("finished");

      setTimeout(() => {
        setFlightValue(0);
        setShowExplosion(false);
        setIsDropping(false);
        setScrollVersion((v) => v + 1);
        setScene("waiting");
        sceneRef.current = "waiting";
      }, 10);
    }
  };

  /** ===== Sound toggle ===== */
  const toggleSound = (e) => {
    const on = e.target.checked;
    setSoundEnabled(on);
    if (SOUND_MUTED) {
      bgAudioRef.current?.pause();
      crashAudioRef.current?.pause();
      return;
    }
    if (on) {
      setIsAudioOmitted(true);
      bgAudioRef.current?.play();
    } else {
      setIsAudioOmitted(false);
      bgAudioRef.current?.pause();
    }
  };

  /** ===== Handle Bet Click ===== */
  const handleBetClick = (side) => {
    const betAmount = side === "left" ? leftBetAmount : rightBetAmount;
    const bettype = "b";
    const setBetErrorMessage =
      side === "left" ? setLeftBetErrorMessage : setRightBetErrorMessage;
    const betArrayRef = side === "left" ? leftBetArrayRef : rightBetArrayRef;
    const setBetArray = side === "left" ? setLeftBetArray : setRightBetArray;

    if (!betAmount || betAmount <= 0) return;

    const liveOdds = Number(payload?.b1);
    const betObj = {
      amount: Number(betAmount),
      profit: 0,
      catmid: payload?.evt_id || roundIdRef.current || roundId,
      round: payload?.evt_id || roundIdRef.current || roundId,
      odds: Number.isFinite(liveOdds) && liveOdds > 0 ? liveOdds : 1.95,
      teamname: "aviator",
      bettype: bettype,
      marketVal: 1,
      game_type: 7,
      market_type: "m",
      game_name: "aviator"
    };

    // Always queue the bet — placed immediately during countdown, or on next flight start
    const newBet = { ...betObj, is_bet: false };
    betArrayRef.current = [...betArrayRef.current, newBet];
    setBetArray([...betArrayRef.current]);

    if (payload?.start == 0) {
      setBetErrorMessage({
        status: true,
        message: "Placing bet..."
      });
      placeAllBetsNowRef.current?.(side === "left", side === "right");
    } else {
      setBetErrorMessage({
        status: true,
        message: "Bet queued for next round"
      });
    }
  };

  /** ===== Place all queued bets in a single API call ===== */
  const placeAllBetsNow = async (hasLeft, hasRight) => {
    if (placingBetsRef.current) return;

    const placedEvtId = roundIdRef.current;
    // Always build bet_array with left at index 0, right at index 1
    const combinedBetArray = [];
    let leftBetObj = null;
    let rightBetObj = null;
    let leftBetsSnapshot = [];
    let rightBetsSnapshot = [];

    if (hasLeft) {
      leftBetsSnapshot = [...leftBetArrayRef.current];
      leftBetArrayRef.current = [];
      setLeftBetArray([]);
      for (const bet of leftBetsSnapshot) {
        if (!bet.is_bet) {
          bet.is_bet = true;
          bet.catmid = placedEvtId || bet.catmid;
          bet.round = placedEvtId || bet.round;
          leftBetObj = bet;
          combinedBetArray.push({
            bet_type: bet.game_type,
            bet_amount: bet.amount,
            section_no: 0
          });
        }
      }
    }

    if (hasRight) {
      rightBetsSnapshot = [...rightBetArrayRef.current];
      rightBetArrayRef.current = [];
      setRightBetArray([]);
      for (const bet of rightBetsSnapshot) {
        if (!bet.is_bet) {
          bet.is_bet = true;
          bet.catmid = placedEvtId || bet.catmid;
          bet.round = placedEvtId || bet.round;
          rightBetObj = bet;
          combinedBetArray.push({
            bet_type: bet.game_type,
            bet_amount: bet.amount,
            section_no: 0
          });
        }
      }
    }

    if (combinedBetArray.length === 0) return;
    placingBetsRef.current = true;

    // A rejected/failed call puts the queued bets back so the player keeps the
    // Cancel button instead of silently losing the stake row.
    const restoreQueuedBets = () => {
      if (hasLeft && leftBetsSnapshot.length) {
        const restored = leftBetsSnapshot.map((bet) => ({
          ...bet,
          is_bet: false,
          catmid: placedEvtId || bet.catmid,
          round: placedEvtId || bet.round
        }));
        leftBetArrayRef.current = restored;
        setLeftBetArray(restored);
      }
      if (hasRight && rightBetsSnapshot.length) {
        const restored = rightBetsSnapshot.map((bet) => ({
          ...bet,
          is_bet: false,
          catmid: placedEvtId || bet.catmid,
          round: placedEvtId || bet.round
        }));
        rightBetArrayRef.current = restored;
        setRightBetArray(restored);
      }
    };

    // Use left or right bet object as base (they share the same round info)
    const baseBet = leftBetObj || rightBetObj;
    const liveOdds = Number(baseBet?.odds);
    const requestBody = {
      ...baseBet,
      amount: Number(baseBet?.amount),
      teamname: "aviator",
      bettype: baseBet?.bettype || "b",
      market_type: "m",
      game_name: "aviator",
      odds: Number.isFinite(liveOdds) && liveOdds > 0 ? liveOdds : 1.95,
      catmid: roundIdRef.current || baseBet.catmid,
      round: roundIdRef.current || baseBet.round,
      bet_array: combinedBetArray
    };

    try {
      const response = await axiosInstance.post(
        "/casino/place-bet-casino",
        requestBody
      );

      if (response.data?.status != null) {
        if (response.data.status) {
          const returnBets = response.data?.data?.return_bets;
          const betsArray = Array.isArray(returnBets)
            ? returnBets
            : returnBets?.bet_id
              ? [returnBets]
              : [];

          // Fixed mapping: index 0 = left, index 1 = right
          // When only one side has a bet, index 0 is that side
          let anyBetConfirmed = false;
          if (hasLeft && hasRight) {
            // Both sides: index 0 = left, index 1 = right
            if (betsArray[0]?.bet_id) {
              leftMainBetIdRef.current = betsArray[0].bet_id;
              leftBetGameIdRef.current = roundIdRef.current || roundId;
              setLeftMainBetId(betsArray[0].bet_id);
              setLeftBetGameId(roundIdRef.current || roundId);
              leftBetPlacedRef.current = true;
              setLeftShowCashoutButton(true);
              setLeftBetButtonShow(false);
              anyBetConfirmed = true;
            }
            if (betsArray[1]?.bet_id) {
              rightMainBetIdRef.current = betsArray[1].bet_id;
              rightBetGameIdRef.current = roundIdRef.current || roundId;
              setRightMainBetId(betsArray[1].bet_id);
              setRightBetGameId(roundIdRef.current || roundId);
              rightBetPlacedRef.current = true;
              setRightShowCashoutButton(true);
              setRightBetButtonShow(false);
              anyBetConfirmed = true;
            }
          } else if (hasLeft) {
            // Only left side: index 0 = left
            if (betsArray[0]?.bet_id) {
              leftMainBetIdRef.current = betsArray[0].bet_id;
              leftBetGameIdRef.current = roundIdRef.current || roundId;
              setLeftMainBetId(betsArray[0].bet_id);
              setLeftBetGameId(roundIdRef.current || roundId);
              leftBetPlacedRef.current = true;
              setLeftShowCashoutButton(true);
              setLeftBetButtonShow(false);
              anyBetConfirmed = true;
            }
          } else if (hasRight) {
            // Only right side: index 0 = right
            if (betsArray[0]?.bet_id) {
              rightMainBetIdRef.current = betsArray[0].bet_id;
              rightBetGameIdRef.current = roundIdRef.current || roundId;
              setRightMainBetId(betsArray[0].bet_id);
              setRightBetGameId(roundIdRef.current || roundId);
              rightBetPlacedRef.current = true;
              setRightShowCashoutButton(true);
              setRightBetButtonShow(false);
              anyBetConfirmed = true;
            }
          }

          const placedAmounts = [];
          if (hasLeft && hasRight) {
            if (leftBetObj) placedAmounts.push(leftBetObj.amount);
            if (rightBetObj) placedAmounts.push(rightBetObj.amount);
          } else if (hasLeft && leftBetObj) {
            placedAmounts.push(leftBetObj.amount);
          } else if (hasRight && rightBetObj) {
            placedAmounts.push(rightBetObj.amount);
          }

          const userRecords = buildUserRecordsFromReturnBets(
            betsArray,
            placedAmounts,
            userName
          );
          if (userRecords.length) {
            userPlacedBetIdsOrderRef.current = userRecords.map(
              (record) => record.betId
            );
            userPlacedBetIdsRef.current = new Set(
              userPlacedBetIdsOrderRef.current.map(String)
            );
            setUserLuckyRecords(userRecords);
          }

          if (
            anyBetConfirmed &&
            roundIdRef.current &&
            betPlacedToastEvtRef.current !== roundIdRef.current
          ) {
            betPlacedToastEvtRef.current = roundIdRef.current;
            showBetPlacedToast(roundIdRef.current, "Order Placed");

            if (hasLeft) setLeftBetErrorMessage({});
            if (hasRight) setRightBetErrorMessage({});
          }
          if (anyBetConfirmed) {
            const roundAlreadyOver =
              pendingLostToastEvtRef.current === placedEvtId ||
              lastFinishedEvtIdRef.current === placedEvtId;
            if (roundAlreadyOver) {
              showLostToastForUncashedBets(placedEvtId);
            } else {
              tryAutoCashoutAtValue(flightValueRef.current);
            }
          } else {
            restoreQueuedBets();
          }
        } else {
          restoreQueuedBets();
          const msg = { status: false, message: response.data.message };
          if (hasLeft) setLeftBetErrorMessage(msg);
          if (hasRight) setRightBetErrorMessage(msg);
        }
      } else {
        restoreQueuedBets();
      }
    } catch (error) {
      restoreQueuedBets();
      const errMsg = {
        status: false,
        message: error.response?.data?.message || "Bet placement failed"
      };
      if (hasLeft) setLeftBetErrorMessage(errMsg);
      if (hasRight) setRightBetErrorMessage(errMsg);
    } finally {
      placingBetsRef.current = false;
    }
  };
  placeAllBetsNowRef.current = placeAllBetsNow;

  /** ===== Cancel queued bet ===== */
  const cancelBet = (side) => {
    if (side === "left") {
      leftBetArrayRef.current = [];
      setLeftBetArray([]);
      setLeftBetErrorMessage({ status: false, message: "Bet cancelled" });
    } else {
      rightBetArrayRef.current = [];
      setRightBetArray([]);
      setRightBetErrorMessage({ status: false, message: "Bet cancelled" });
    }
  };

  /** ===== Cashout handler ===== */
  const cashOutNow = async (side, multiplierOverride) => {
    const isCashingOutRef =
      side === "left" ? leftIsCashingOutRef : rightIsCashingOutRef;
    const mainBetIdRef =
      side === "left" ? leftMainBetIdRef : rightMainBetIdRef;
    const betGameIdRef =
      side === "left" ? leftBetGameIdRef : rightBetGameIdRef;
    const mainBetId =
      mainBetIdRef.current ||
      (side === "left" ? leftMainBetId : rightMainBetId);
    const betAmount = side === "left" ? leftBetAmount : rightBetAmount;
    const setIsCashingOut =
      side === "left" ? setLeftIsCashingOut : setRightIsCashingOut;
    const setShowCashoutButton =
      side === "left" ? setLeftShowCashoutButton : setRightShowCashoutButton;
    const setMainBetId = side === "left" ? setLeftMainBetId : setRightMainBetId;
    const betPlacedRef = side === "left" ? leftBetPlacedRef : rightBetPlacedRef;
    const setBetErrorMessage =
      side === "left" ? setLeftBetErrorMessage : setRightBetErrorMessage;
    const setCashOutAmount =
      side === "left" ? setLeftCashOutAmount : setRightCashOutAmount;
    const setBetButtonShow =
      side === "left" ? setLeftBetButtonShow : setRightBetButtonShow;
    const betArrayRef = side === "left" ? leftBetArrayRef : rightBetArrayRef;
    const setBetArray = side === "left" ? setLeftBetArray : setRightBetArray;

    if (!mainBetId || isCashingOutRef.current) return;

    const rawMultiplier =
      multiplierOverride != null && Number(multiplierOverride) > 0
        ? Number(multiplierOverride)
        : Number(flightValueRef.current);
    if (!(rawMultiplier + 0.0001 >= CASHOUT_MIN_MULTIPLIER)) return;
    const incrementor = rawMultiplier.toFixed(2);
    const gameId =
      betGameIdRef.current || roundIdRef.current || roundId;
    const betId = mainBetId;

    setIsCashingOut(true);
    isCashingOutRef.current = true;
    setShowCashoutButton(false);
    setBetButtonShow(true);
    setCashOutAmount("");

    try {
      const response = await axiosInstance.post("/casino/cash-out", {
        game_id: gameId,
        bet_id: betId,
        gametype: "aviator",
        win_multiplier: incrementor
      });

      const result = response.data;

      if (result.isSuccess && result.error === 0) {
        const wonAmount =
          result.data?.cash_out_amount ?? result.cash_out_amount;
        if (wonAmount != null && wonAmount !== "") {
          showWinToast(wonAmount);
        }

        setBetErrorMessage({});

        setShowCashoutButton(false);
        setBetButtonShow(true);
        mainBetIdRef.current = "";
        setMainBetId("");
        setCashOutAmount("");
        betPlacedRef.current = false;

        betArrayRef.current = [];
        setBetArray([]);

        const cashedAmount = parseFloat(betAmount);
        setUserLuckyRecords((prev) =>
          prev.map((bet) => {
            if (String(bet.betId) !== String(mainBetId) || bet.locked) {
              return bet;
            }
            const amount = bet.amountRaw || cashedAmount;
            return {
              ...bet,
              rate: incrementor,
              profit: calcLuckyPlayerProfit(incrementor, amount),
              locked: true
            };
          })
        );
      } else {
        const crashAt = Number(maxFlightRef.current);
        const requested = parseFloat(incrementor);
        const cashedAtOrBelowCrash =
          Number.isFinite(crashAt) &&
          Number.isFinite(requested) &&
          requested <= crashAt + 0.001;
        if (isPostCrashScene() && !cashedAtOrBelowCrash) {
          isCashingOutRef.current = false;
          showLostToastForUncashedBets();
        }
        const stillFlying = sceneRef.current === "flight";
        setShowCashoutButton(stillFlying);
        if (stillFlying) setBetButtonShow(false);
        setBetErrorMessage({
          status: false,
          message: result.message || "Cashout failed"
        });
      }
    } catch (error) {
      console.error("Cashout error:", error);
      const crashAt = Number(maxFlightRef.current);
      const requested = parseFloat(incrementor);
      const cashedAtOrBelowCrash =
        Number.isFinite(crashAt) &&
        Number.isFinite(requested) &&
        requested <= crashAt + 0.001;
      if (isPostCrashScene() && !cashedAtOrBelowCrash) {
        isCashingOutRef.current = false;
        showLostToastForUncashedBets();
      }
      const stillFlying = sceneRef.current === "flight";
      setShowCashoutButton(stillFlying);
      if (stillFlying) setBetButtonShow(false);
      setBetErrorMessage({
        status: false,
        message: error.response?.data?.message || "Cashout request failed"
      });
    } finally {
      isCashingOutRef.current = false;
      setIsCashingOut(false);
    }
  };
  cashOutNowRef.current = cashOutNow;

  /** ===== Loading screen timer ===== */
  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(t);
  }, []);

  /** ===== Re-sync queued bets on game over (preserve queued bets for next round) ===== */
  useEffect(() => {
    if (
      scene === "crash-explode" ||
      scene === "crash-drop" ||
      scene === "finished"
    ) {
      // Do NOT clear betPlacedRef or showCashoutButton here — the server may
      // have already started the next round (start==0 → start==1) while the
      // client crash animation is still playing. Clearing here would undo the
      // bet state set by placeAllBetsNow for the new round.
      // These are properly reset by the start==0 handler and cashOutNow instead.

      // Re-sync betArray state from ref so Cancel button shows for queued bets
      setLeftBetArray([...leftBetArrayRef.current]);
      setRightBetArray([...rightBetArrayRef.current]);
    }
  }, [scene]);

  /** ===== Show cashout button when bet is confirmed and flight is active ===== */
  useEffect(() => {
    if (scene !== "flight") return;
    if (
      !leftIsCashingOutRef.current &&
      leftBetPlacedRef.current &&
      (leftMainBetId || leftMainBetIdRef.current)
    ) {
      setLeftShowCashoutButton(true);
    }
    if (
      !rightIsCashingOutRef.current &&
      rightBetPlacedRef.current &&
      (rightMainBetId || rightMainBetIdRef.current)
    ) {
      setRightShowCashoutButton(true);
    }
  }, [scene, leftMainBetId, rightMainBetId]);

  /** ===== Auto Cashout: trigger cashout when flight value meets threshold ===== */
  useEffect(() => {
    if (scene !== "flight" || flightValue < CASHOUT_MIN_MULTIPLIER) return;
    tryAutoCashoutAtValue(Math.min(flightValue, lastServerRunningRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flightValue,
    scene,
    leftShowCashoutButton,
    rightShowCashoutButton,
    leftIsCashingOut,
    rightIsCashingOut
  ]);

  /** ===== Update cashout amount during flight ===== */
  useEffect(() => {
    if (scene === "flight" && flightValue >= 1) {
      if (leftShowCashoutButton && leftBetAmount > 0) {
        setLeftCashOutAmount(
          (parseFloat(leftBetAmount) * flightValue).toFixed(2)
        );
      }
      if (rightShowCashoutButton && rightBetAmount > 0) {
        setRightCashOutAmount(
          (parseFloat(rightBetAmount) * flightValue).toFixed(2)
        );
      }
    }
  }, [
    flightValue,
    scene,
    leftShowCashoutButton,
    rightShowCashoutButton,
    leftBetAmount,
    rightBetAmount
  ]);

  /** Convenience flags */
  const showTimer =
    scene === "flight" || scene === "crash-explode" || scene === "crash-drop";
  const hasValidPayload = payload != null && !!payload?.evt_id;

  useEffect(() => {
    document.documentElement.classList.add("aviator-bg-black");
    /* Avoid Bootstrap `h-100` on <html> — it locks height and blocks scrolling to page end on mobile */
    document.documentElement.classList.add("aviator-route-scroll");

    return () => {
      document.documentElement.classList.remove("aviator-bg-black");
      document.documentElement.classList.remove("aviator-route-scroll");
    };
  }, []);

  /** ===== Reusable Bet Panel render function (not a component, to avoid remount on every render) ===== */
  const renderBetPanel = (side) => {
    const isLeft = side === "left";
    const betAmount = isLeft ? leftBetAmount : rightBetAmount;
    const setBetAmount = isLeft ? setLeftBetAmount : setRightBetAmount;
    const betArray = isLeft ? leftBetArray : rightBetArray;
    const showCashoutButton = isLeft
      ? leftShowCashoutButton
      : rightShowCashoutButton;
    const betButtonShow = isLeft ? leftBetButtonShow : rightBetButtonShow;
    const mainBetId = isLeft ? leftMainBetId : rightMainBetId;
    const cashOutAmount = isLeft ? leftCashOutAmount : rightCashOutAmount;
    const isCashingOut = isLeft ? leftIsCashingOut : rightIsCashingOut;
    const betErrorMessageObj = isLeft
      ? leftBetErrorMessage
      : rightBetErrorMessage;
    const autoBet = isLeft ? leftAutoBet : rightAutoBet;
    const autoCashout = isLeft ? leftAutoCashout : rightAutoCashout;
    const autoCashoutAt = isLeft ? leftAutoCashoutAt : rightAutoCashoutAt;
    const setAutoBetState = (val) => {
      if (isLeft) {
        setLeftAutoBet(val);
        leftAutoBetRef.current = val;
      } else {
        setRightAutoBet(val);
        rightAutoBetRef.current = val;
      }
    };
    const setAutoCashoutState = (val) => {
      if (isLeft) {
        setLeftAutoCashout(val);
        leftAutoCashoutRef.current = val;
      } else {
        setRightAutoCashout(val);
        rightAutoCashoutRef.current = val;
      }
    };
    const setAutoCashoutAtState = (val) => {
      if (isLeft) {
        setLeftAutoCashoutAt(val);
        leftAutoCashoutAtRef.current = val;
      } else {
        setRightAutoCashoutAt(val);
        rightAutoCashoutAtRef.current = val;
      }
    };
    const btnColor = "btn-success";

    return (
      <div className="col-6 col-md-6 mb-4 px-2 py-1 aviator-bet-panel">
        <div className="market-runners w-100 d-flex flex-column align-items-stretch">
          {/* Bet amount input */}
          <div className="row mb-2 mx-0 w-100">
            <div className="col-12 px-0">
              <div className="input-group w-100">
                <div className="AVEDI-input-group w-100">
                  <button
                    type="button"
                    onClick={() => setBetAmount((v) => Math.max(1, v - 100))}
                  >
                    <svg
                      width="28"
                      height="40"
                      viewBox="0 0 28 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="AVE-svg-icon"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20Z"
                        fill="#F7F9FB"
                      />
                    </svg>
                  </button>
                  <input
                    type="text"
                    min="1"
                    max="1000000"
                    className="AVEDI-input-field"
                    value={betAmount}
                    onInput={(e) => {
                      const val = e.target.value
                        .replace(/[^0-9.]/g, "")
                        .replace(/(\..*?)\..*/g, "$1");
                      setBetAmount(
                        val === "" ? "" : Math.max(1, parseInt(val) || 0)
                      );
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setBetAmount((v) => v + 100)}
                  >
                    <svg
                      width="28"
                      height="40"
                      viewBox="0 0 28 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="AVE-svg-icon"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M14 11.75C14.1989 11.75 14.3897 11.829 14.5303 11.9697C14.671 12.1103 14.75 12.3011 14.75 12.5V19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H14.75V27.5C14.75 27.6989 14.671 27.8897 14.5303 28.0303C14.3897 28.171 14.1989 28.25 14 28.25C13.8011 28.25 13.6103 28.171 13.4697 28.0303C13.329 27.8897 13.25 27.6989 13.25 27.5V20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H13.25V12.5C13.25 12.3011 13.329 12.1103 13.4697 11.9697C13.6103 11.829 13.8011 11.75 14 11.75Z"
                        fill="#F7F9FB"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Incremental amount buttons */}
          <div className="row mb-2 mx-0 w-100 g-2">
            {[200, 500, 1000, 2000].map((amt) => (
              <div key={amt} className="col-6 col-md-3 px-0">
                <button
                  type="button"
                  className="btn btn-lg btn-outline-light w-100 p-0"
                  onClick={() => setBetAmount(amt)}
                >
                  <span className="amt">{amt}</span>
                </button>
              </div>
            ))}
          </div>

          {/* Bet / Cancel / Cashout buttons */}
          <div className="row mb-2 mx-0 w-100">
            <div className="col-12 px-0">
              <div role="group" className="btn-group w-100 aviator-bet-actions">
                {(() => {
                  const showCancel = betArray.length > 0 && !showCashoutButton;
                  const showCashout =
                    showCashoutButton && scene === "flight" && !isCashingOut;
                  const showBet = !showCancel && !showCashout;
                  return (
                    <>
                      {/* Bet / Next button — always shown as disabled fallback */}
                      <div
                        style={{
                          width: "100%",
                          display: showBet ? "block" : "none"
                        }}
                      >
                        <button
                          type="button"
                          className={`btn-lg w-100 btn ${btnColor}`}
                          onClick={() => handleBetClick(side)}
                          disabled={!betButtonShow || !hasValidPayload}
                        >
                          <span className="shimmer-container">
                            <span className="shimmer"></span>
                          </span>
                          <span className="text-lg font-semibold text-slate-50">
                            {scene === "countdown" && hasValidPayload
                              ? "Place"
                              : "Next"}
                          </span>
                        </button>
                      </div>

                      {/* Cancel button */}
                      <div
                        style={{
                          width: "100%",
                          display: showCancel ? "block" : "none"
                        }}
                      >
                        <button
                          type="button"
                          className="btn-lg w-100 btn btn-info"
                          onClick={() => cancelBet(side)}
                        >
                          <span className="shimmer-container">
                            <span className="shimmer"></span>
                          </span>
                          <span className="text-lg font-semibold text-slate-50">
                            Cancel
                          </span>
                        </button>
                      </div>

                      {/* Cashout button */}
                      <div
                        style={{
                          width: "100%",
                          display: showCashout ? "block" : "none"
                        }}
                      >
                        <input
                          type="hidden"
                          name={`${side}_bet_id`}
                          value={mainBetId}
                        />
                        <button
                          type="button"
                          className="btn-lg w-100 pl-0 pr-0 btn btn-warning"
                          onClick={() => cashOutNow(side)}
                          disabled={isCashingOut}
                        >
                          <span className="shimmer-container">
                            <span className="shimmer"></span>
                          </span>
                          <span className="text-lg font-semibold text-slate-50 mobile-text">
                            Cashout
                          </span>
                          <span className="mobile-text">
                            {cashOutAmount ? ` ${cashOutAmount}` : ""}
                          </span>
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Error/Success message */}
          {betErrorMessageObj.message && (
            <div
              className={`alert ${
                betErrorMessageObj.status ? "alert-success" : "alert-danger"
              } m-0 p-1`}
              style={{ fontSize: "11px" }}
            >
              {betErrorMessageObj.message}
            </div>
          )}

          {/* Auto Bet / Auto Cashout toggles */}
          <div className="mb-2" style={{ color: "white", paddingTop: "5px" }}>
            <div className="col-12 p-0 d-flex align-items-center">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={autoBet}
                  onChange={(e) => setAutoBetState(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
              <label>Auto Bet</label>
            </div>
            <div className="col-12 p-0 d-flex align-items-center">
              <label className="switch mr-2">
                <input
                  type="checkbox"
                  checked={autoCashout}
                  onChange={(e) => setAutoCashoutState(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
              <label>Auto Cashout</label>
            </div>
            <div
              className="w-100"
              style={{
                border: "1px solid white",
                borderRadius: "10px",
                padding: "5px",
                display: autoCashout ? "block" : "none"
              }}
            >
              <div className="form-group mb-0 w-100">
                <label style={{ fontSize: "12px" }}>
                  Cashout at multiplier
                </label>
                <div className="input-group w-100">
                  <div className="AVEDI-input-group w-100">
                    <button
                      type="button"
                      onClick={() =>
                        setAutoCashoutAtState(
                          Math.max(1, +(autoCashoutAt - 0.02).toFixed(2))
                        )
                      }
                    >
                      <svg
                        width="28"
                        height="40"
                        viewBox="0 0 28 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="AVE-svg-icon"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20Z"
                          fill="#F7F9FB"
                        />
                      </svg>
                    </button>
                    <input
                      type="number"
                      min="1.00"
                      max="100"
                      step="0.02"
                      className="AVEDI-input-field"
                      value={autoCashoutAt}
                      onChange={(e) =>
                        setAutoCashoutAtState(parseFloat(e.target.value) || 1.1)
                      }
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setAutoCashoutAtState(
                          +(autoCashoutAt + 0.02).toFixed(2)
                        )
                      }
                    >
                      <svg
                        width="28"
                        height="40"
                        viewBox="0 0 28 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="AVE-svg-icon"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M14 11.75C14.1989 11.75 14.3897 11.829 14.5303 11.9697C14.671 12.1103 14.75 12.3011 14.75 12.5V19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H14.75V27.5C14.75 27.6989 14.671 27.8897 14.5303 28.0303C14.3897 28.171 14.1989 28.25 14 28.25C13.8011 28.25 13.6103 28.171 13.4697 28.0303C13.329 27.8897 13.25 27.6989 13.25 27.5V20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H13.25V12.5C13.25 12.3011 13.329 12.1103 13.4697 11.9697C13.6103 11.829 13.8011 11.75 14 11.75Z"
                          fill="#F7F9FB"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <style>
          {`
            @media (max-width:576px){
              .footer-mob{
                margin-left: 10px;
                margin-right: 10px;
              }
            .left-content {
                  margin: 10px !important;
              }
              .col-md-8{
                padding-left: 5px;
                padding-right: 5px;
              }
              .col-md-4{
                padding-left: 5px;
                padding-right: 5px;
              }
              .result-itemAV { 
                padding: 7px !important;
              }
            }
            body {
              line-height: normal;
            }

            .counter-num {
              position: absolute;
              top: 20%; left: 50%;
              transform: translate(-50%, -50%);
              font-size: 80px;
              font-weight: 700;
              color: #fff;
              z-index: 5;
              text-shadow: 0 0 20px rgba(0,0,0,0.5);
            }
          `}
        </style>
      </div>
    );
  };

  const overlayBlurActive =
    isConnected && (scene === "countdown" || scene === "waiting");

  return (
    <>
      <div className="aviator-scope aviator-bg-black">
        <ClientLayoutWithOutHeader>
          {/* Loading Screen */}
          {isLoading && (
            <div
              style={{
                background: "#000",
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <img
                src="/assets/images/tpstudio/aviatorstat.webp"
                alt="Loading"
                className="smooth-blink"
              />
            </div>
          )}

          <div className="details-tabs-wrapper1">
            <div className="main-page">
              <div className="row no-gutters">
                <div className="col-md-12">
                  <div className="bodyheight">
                    <div className="wrapper">
                      <TPStudioSidebar open={open} />
                    </div>

                    <div className="lobby-ctn BPGmaindiv">
                      <TpStudioHeader setOpen={setOpen} open={open} />
                    </div>

                    <div className="card-body">
                      <div className="row aviator-game-row">
                        <div className="col-md-8">
                          <div className="footer-divbpg border-gradient-footer border-gradient-purple-footer footer-mob">
                            <div className="col-6 col-md-6 titlebarname  inline-block Gameidcol mt-2">
                              <p>Aviator</p>
                            </div>

                            <div
                              className="col-6 col-md-6 inline-block Gameidcol text-right"
                              style={{ marginTop: "4px" }}
                            >
                              <div
                                role="group"
                                aria-label="Second group"
                                className="btn-group btn-group-sm mr-2"
                              >
                                <button
                                  style={{
                                    fontSize: "14px",
                                    backgroundColor: "black"
                                  }}
                                  type="button"
                                  id="Alltab"
                                  className={
                                    activeTabIndex == 1
                                      ? "tablink btn btn-primary bbactive"
                                      : "tablink btn btn-primary"
                                  }
                                  onClick={() => activeTab(1)}
                                >
                                  Game
                                </button>
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  id="RESULTStab"
                                  className={
                                    activeTabIndex == 2
                                      ? "tablink btn btn-primary bbactive"
                                      : "tablink btn btn-primary"
                                  }
                                  onClick={() => activeTab(2)}
                                >
                                  Results
                                </button>
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  className="tablink btn btn-primary"
                                  onClick={() => setShowRules(true)}
                                >
                                  Rules
                                </button>

                                <AviatorRules
                                  show={showRules}
                                  onClose={() => setShowRules(false)}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Audio */}
                          <audio
                            ref={bgAudioRef}
                            src={BgSound}
                            loop
                            muted={SOUND_MUTED}
                          />
                          <audio
                            ref={crashAudioRef}
                            src={CrashSound}
                            muted={SOUND_MUTED}
                          />

                          {/* Scene */}
                          <div
                            className="cards-layout blackjack font-familyAVE"
                            style={{
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            <div className="scroll-containeraviator">
                              <div
                                className="row"
                                style={{
                                  position: "absolute",
                                  width: "100%",
                                  padding: "10px"
                                }}
                              >
                                <div className="col-1">
                                  <div className="Connectedclients mt-1 ml-1 float-start">
                                    <i
                                      aria-hidden="true"
                                      className="fa fa-user mr-1"
                                      style={{ width: "14px", color: "white" }}
                                    />
                                    <b>
                                      <span id="UsersCount" className="number">
                                        {luckyPlayerCount}
                                      </span>
                                    </b>
                                  </div>
                                </div>
                                <div className="col-10"></div>
                                <div className="col-1">
                                  <div
                                    style={{
                                      float: "inline-end",
                                      position: "relative",
                                      zIndex: 1000
                                    }}
                                  >
                                    <i className="AVI-icon__signal-strength AVI-signal-0">
                                      <span
                                        className="AVI-bar-1"
                                        style={{
                                          backgroundColor: payload
                                            ? "rgb(0, 255, 0)"
                                            : "rgb(255, 0, 0)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-2"
                                        style={{
                                          backgroundColor: payload
                                            ? "rgb(0, 255, 0)"
                                            : "rgb(255, 0, 0)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-3"
                                        style={{
                                          backgroundColor: payload
                                            ? "rgb(0, 255, 0)"
                                            : "rgb(204, 204, 204)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-4"
                                        style={{
                                          backgroundColor: payload
                                            ? "rgb(0, 255, 0)"
                                            : "rgb(204, 204, 204)"
                                        }}
                                      ></span>
                                    </i>
                                  </div>
                                </div>
                              </div>

                              {/* Overlay: disconnected */}
                              {!isConnected && (
                                <div
                                  className="overlayaviator"
                                  style={{
                                    zIndex: 999,
                                    background: "rgba(0,0,0,0.85)"
                                  }}
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <span
                                      className="overlay-textaviator"
                                      style={{ color: "#ff4444" }}
                                    >
                                      <b>Connection Lost</b>
                                    </span>
                                    <br />
                                    <span
                                      className="overlay-textaviator"
                                      style={{
                                        fontSize: "14px",
                                        color: "#ccc"
                                      }}
                                    >
                                      Reconnecting...
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Overlay: waiting / countdown */}
                              {isConnected &&
                                (scene === "countdown" ||
                                  scene === "waiting") && (
                                  <div className="overlayaviator">
                                    <div
                                      style={{
                                        textAlign: "center",
                                        width: "100%"
                                      }}
                                    >
                                      <span className="overlay-textaviator">
                                        <b>
                                          {scene === "waiting"
                                            ? "STARTING NEXT ROUND"
                                            : launchCountdown > 0
                                            ? `STARTING NEXT ROUND IN ${launchCountdown}`
                                            : "STARTING NEXT ROUND"}
                                        </b>
                                      </span>
                                      {scene === "waiting" && (
                                        <div className="aviator-fun-fact-banner">
                                          {FUN_FACT_OVERLAY_TEXT}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                              {(scene === "crash-explode" ||
                                scene === "crash-drop") &&
                                flightValue >= 1 && (
                                  <div className={`flight-timer timer-crash`}>
                                    <div className="crash-text">CRASHED</div>
                                    {flightValue.toFixed(2)}x
                                  </div>
                                )}
                              {scene === "flight" && (
                                <div className="counter-num">
                                  {(flightValue >= 1 ? flightValue : 1).toFixed(
                                    2
                                  )}
                                  x
                                </div>
                              )}
                              <div
                                id="toshakecontent"
                                className={`w-100 p-0${
                                  overlayBlurActive
                                    ? " aviator-overlay-blur-target"
                                    : ""
                                }`}
                              >
                                {scene === "loading" ||
                                scene === "waiting" ||
                                scene === "countdown" ? (
                                  <div
                                    className="aviator-static-bg-layer"
                                    style={{
                                      backgroundImage: `url(${AVIATOR_SCROLL_BG})`
                                    }}
                                    aria-hidden
                                  />
                                ) : (
                                  <div
                                    key={scrollVersion}
                                    className={`scroll-contentaviator ${
                                      scene === "flight" ? "running" : "paused"
                                    }`}
                                  >
                                    <img
                                      src={AVIATOR_SCROLL_BG}
                                      alt="slice-0"
                                    />
                                    {Array.from({ length: 18 }).map((_, i) => (
                                      <img
                                        key={i}
                                        src={AVIATOR_SCROLL_BG}
                                        alt={`slice-${i + 1}`}
                                      />
                                    ))}
                                    <img
                                      src={AVIATOR_SCROLL_BG}
                                      alt="slice-end"
                                    />
                                  </div>
                                )}
                              </div>

                              <div
                                id="centerImageaviator"
                                className={`containerAVIM center-imageaviator ${
                                  scene === "countdown" ? "blurred" : ""
                                }${
                                  overlayBlurActive
                                    ? " aviator-overlay-blur-target"
                                    : ""
                                }`}
                              >
                                {scene === "crash-explode" && showExplosion && (
                                  <img src={Explosion} alt="Explosion" />
                                )}

                                <div
                                  className={`${
                                    scene === "flight" && !isDropping
                                      ? "plane-sway"
                                      : ""
                                  } ${
                                    isDropping ? "plane-drop grayscale" : ""
                                  }`}
                                  onAnimationEnd={
                                    isDropping ? handleDropEnd : undefined
                                  }
                                >
                                  <div id="planeimage" className="contentAVIM">
                                    {/* Thrust SVG */}
                                    <svg
                                      id="animated-svg"
                                      width="55"
                                      height="6"
                                      viewBox="0 0 55 6"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                      className={
                                        scene === "flight"
                                          ? "thrust-running"
                                          : "thrust-paused"
                                      }
                                    >
                                      <path
                                        d="M0 1.905C4.41 1.571 9.29 1.27 13.735 1.07C14.871 1.003 16.007 0.936 17.177 0.836C19.483 0.668 21.755 0.836 24.028 1.17C25.164 1.337 26.3 1.571 27.47 1.905V2.139C24.095 3.075 20.652 3.409 17.177 3.208C15.205 3.041 12.331 2.908 10.326 2.807C6.984 2.607 3.342 2.373 0.033 2.139C0 2.139 0 1.905 0 1.905Z"
                                        fill="#ED1C24"
                                      />
                                      <path
                                        d="M54.94 2.139C50.528 2.473 45.649 2.774 41.205 2.974C40.068 3.041 38.932 3.108 37.763 3.208C35.457 3.375 33.184 3.208 30.912 2.874C29.776 2.707 28.639 2.473 27.47 2.139V1.905C30.845 0.969 34.287 0.635 37.763 0.835C39.734 1.002 42.608 1.136 44.613 1.236C47.955 1.437 51.598 1.671 54.906 1.905C54.94 1.905 54.94 2.139 54.94 2.139Z"
                                        fill="#9b8687ff"
                                      />
                                    </svg>

                                    {/* Plane */}
                                    <img
                                      src={Newplane}
                                      alt="Plane"
                                      className="w-36 h-36"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Sound toggle */}
                              <label
                                htmlFor="AvxSoundcontrol"
                                className={`fa fa-inverse sound-icon fa-2x ${
                                  soundEnabled
                                    ? "fa-volume-up"
                                    : "fa-volume-off"
                                }`}
                                style={{
                                  position: "absolute",
                                  right: 8,
                                  bottom: 8,
                                  zIndex: 100,
                                  cursor: "pointer"
                                }}
                                title="Toggle sound"
                              >
                                <input
                                  id="AvxSoundcontrol"
                                  type="checkbox"
                                  checked={soundEnabled}
                                  onChange={toggleSound}
                                  style={{ display: "none" }}
                                />
                              </label>
                            </div>
                          </div>
                          {/* /Scene */}

                          {/* Results history & Round ID */}
                          <center
                            style={{
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            <div className="footer-divbpg border-gradient-footer border-gradient-purple-footer">
                              <div className="buttons-div">
                                {lastTenResults.length > 0
                                  ? lastTenResults.map((val, idx) => (
                                      <button
                                        key={idx}
                                        className="video-button border-gradient-btn border-gradient-purple-btn"
                                      >
                                        {val.toFixed(2)}
                                      </button>
                                    ))
                                  : null}
                              </div>
                              <div className="round-div">
                                <div className="label">Round ID:</div>
                                <div className="id">{roundId || "--"}</div>
                              </div>
                            </div>
                          </center>

                          {/* Two-sided betting panels */}
                          <div
                            className="row m-0 p-0 roulette"
                            style={{
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            {/* Left side */}
                            {renderBetPanel("left")}

                            {/* Right side */}
                            {renderBetPanel("right")}
                          </div>

                          <div
                            style={{
                              display: activeTabIndex == 2 ? "" : "none"
                            }}
                          >
                            <AvaitorResult resultData={resultData} />
                          </div>
                        </div>
                        {/* Right sidebar - Lucky Players */}
                        <div className="col-md-4 aviator-lucky-players-col">
                          <div className="right-content">
                            <div className="card" style={{ border: "0px" }}>
                              <div
                                className="card-header"
                                id="headingTwo"
                                style={{
                                  padding: "0px",
                                  background: "#23292d"
                                }}
                              >
                                <h2
                                  className="mb-0"
                                  style={{
                                    background: "var(--theme-color)",
                                    borderRadius: "0px"
                                  }}
                                >
                                  <button
                                    className="btn btn-link btn-block text-left"
                                    type="button"
                                    style={{
                                      color: "white",
                                      textDecoration: "none"
                                    }}
                                  >
                                    Lucky Players{" "}
                                    <span id="totalLuckyPlayers">
                                      {luckyPlayerCount}
                                    </span>
                                  </button>
                                </h2>
                              </div>
                              <div style={{ backgroundColor: "#2e3438" }}>
                                <div
                                  className="card-body"
                                  style={{ padding: 0, minHeight: "40px" }}
                                >
                                  <div
                                    id="refreshhistory"
                                    className="aviator-lucky-players-scroll"
                                    style={{ paddingLeft: 0, width: "100%" }}
                                  >
                                    <table className="table blastoff-table">
                                      <thead>
                                        <tr>
                                          <th>User</th>
                                          <th>Size</th>
                                          <th>Price</th>
                                          <th>Profit</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {betsList && betsList.length > 0
                                          ? betsList.map((bet) => (
                                              <tr
                                                key={bet.id}
                                                style={{
                                                  backgroundColor: "#eff8ff",
                                                  color: "#000",
                                                  fontSize: "13px",
                                                  textAlign: "center"
                                                }}
                                              >
                                                <td
                                                  style={{
                                                    textAlign: "left",
                                                    fontSize: "13px"
                                                  }}
                                                >
                                                  {bet.uname}
                                                </td>
                                                <td
                                                  style={{ fontSize: "13px" }}
                                                >
                                                  {bet.amount}
                                                </td>
                                                <td
                                                  style={{ fontSize: "13px" }}
                                                >
                                                  {bet.rate}
                                                </td>
                                                <td
                                                  style={{
                                                    textAlign: "center",
                                                    fontSize: "13px"
                                                  }}
                                                >
                                                  {bet.profit}
                                                </td>
                                              </tr>
                                            ))
                                          : null}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>{" "}
                        {/*  ends here  */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ClientLayoutWithOutHeader>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={1500}
        hideProgressBar={false}
        closeButton={false}
        newestOnTop
        limit={3}
      />

      <style>
        {`
          .av-win-toast,
          .av-bet-toast,
          .av-lost-toast {
            border-radius: 8px !important;
            padding: 12px 20px !important;
            min-height: unset !important;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.38) !important;
            overflow: hidden;
          }
          .av-win-toast {
            background: #2d7a3a !important;
          }
          .av-bet-toast {
            background: #43a047 !important;
          }
          .av-lost-toast {
            background: #c62828 !important;
          }
          .av-win-toast .Toastify__toast-body,
          .av-bet-toast .Toastify__toast-body,
          .av-lost-toast .Toastify__toast-body {
            padding: 0 !important;
            margin: 0 !important;
          }
          .av-win-toast .Toastify__progress-bar,
          .av-bet-toast .Toastify__progress-bar,
          .av-lost-toast .Toastify__progress-bar {
            background: rgba(0, 0, 0, 0.2) !important;
            height: 4px;
          }
          .av-toast-body {
            display: flex;
            align-items: center;
            gap: 12px;
            color: #ffffff;
            font-size: 18px;
            font-weight: 600;
            line-height: 1.2;
            white-space: nowrap;
          }
          .av-toast-icon {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
          }
          .av-toast-icon svg {
            display: block;
            width: 24px;
            height: 24px;
          }
          .av-toast-text {
            padding-top: 1px;
          }

          .aviator-scope .scroll-containeraviator .Connectedclients {
            position: relative;
            top: -8px;
            left: -24px;
          }
          .aviator-scope .scroll-containeraviator .row > .col-1:last-child > div {
            position: relative !important;
            top: -10px;
            right: -24px;
          }
          .aviator-scope .scroll-containeraviator .AVI-icon__signal-strength {
            transform: scale(0.9);
            transform-origin: center;
          }
          .aviator-scope .scroll-containeraviator label.sound-icon[for="AvxSoundcontrol"] {
            transform: scale(0.76);
            transform-origin: bottom right;
            bottom: -33px !important;
            right: 19px !important;
          }
        `}
      </style>
    </>
  );
}
